"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const handleLinkClick = () => setIsMobileMenuOpen(false);
  if (loading) {
    return <div data-section-id="84683" className="flex gap-4">
        <div className="h-6 w-16 animate-pulse rounded-md bg-muted/50" />
        <div className="h-6 w-16 animate-pulse rounded-md bg-muted/50" />
        <div className="h-6 w-16 animate-pulse rounded-md bg-muted/50" />
      </div>;
  }
  const customLinkStyle = "relative font-serif text-base font-medium text-foreground/90 tracking-wide transition-colors py-2 px-1 after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-secondary after:transition-all after:duration-300 hover:text-foreground hover:after:w-full";
  return <>
      {}
      <div className="hidden md:block">
        <NavigationMenu viewport={false}>
          <NavigationMenuList className="gap-10">
            {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className={customLinkStyle}>
                    {item.label}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>)}
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      {}
      <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-secondary/10" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle Menu">
        {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      {}
      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-2xl md:hidden py-6 px-6 flex flex-col gap-2 animate-in slide-in-from-top-2">
          {menuItems.map((item, i) => <div key={item.id} data-index={i}>
              <Link to={item.href || "#"} className="block font-serif text-xl text-foreground/90 hover:text-secondary py-4 border-b border-border/40 last:border-0 transition-colors" onClick={handleLinkClick}>
                {item.label}
              </Link>
            </div>)}
        </div>}
    </>;
}
export default function SiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="sanctuary-header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 ease-in-out", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-md py-3" : "bg-background border-b border-border/10 py-5")}>
      <div className="container mx-auto px-4 md:px-8 flex items-center justify-between">
        {}
        <Link to="/HomePage" className="flex items-center shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary rounded-sm transition-transform hover:scale-[1.02]">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590036/img/st_sampson_logo.png" alt="St. Sampson Senior Aid Foundation Logo" className="h-10 w-auto object-contain md:h-12" />
        </Link>

        {}
        <MenuProvider menu_id="header_menu">
          <HeaderMenuConsumer />
        </MenuProvider>
      </div>
    </header>;
}